import { Injectable, OnDestroy } from "@angular/core";
import { UserService } from "./user.service";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { AuthService } from "../../auth/auth.service";
import { UserCache } from "./user.cache";

/** Provides methods to work with user data. */
export interface IUserManager {
	/**
	 * Refreshes the repair facilities for the user.
	 * @see RepairFacility
	 * @see UserService.getRepairFacilities
	 */
	refreshRepairFacilities(): void;
	/**
	 * Refreshes the contacts for the user.
	 * @see UserContact
	 * @see UserService.getContacts
	 */
	refreshContacts(): void;
}

@Injectable({
	providedIn: 'root'
})
export class UserManager implements IUserManager, OnDestroy {
	/**
	 * A subject that can be used to automatically end subscriptions when the component is destroyed.
	 * @private
	 */
	private readonly _destroying$ = new Subject<void>();

	// Updating flags.
	public updatingRepairFacilities: boolean;
	public updatingContacts: boolean;

	constructor(private userService: UserService,
	            private userCache: UserCache,
	            private authService: AuthService) {
		this.init();
	}

	// Initializes the user manager.
	init(): void {
		this.authService.loginStatusChanged
			.pipe(takeUntil(this._destroying$))
			// Refresh all user data when the login status changes.
			.subscribe(() => this.refreshAll());
		// If the component starts with the user logged in, perform a refresh.
		if (this.authService.isLoggedIn()) this.refreshAll();
	}

	ngOnDestroy(): void {
		this._destroying$.next(undefined);
		this._destroying$.complete();
	}

	// Refreshes all user data.
	public refreshAll(): void {
		// If not logged in, don't perform a refresh.
		if (!this.authService.isLoggedIn()) return;
		//TODO: If not logged in, wait until a login is complete.
		
		this.refreshRepairFacilities();
		this.refreshContacts();
	}

	public refreshRepairFacilities(): void {
		// If not logged in, don't perform a refresh.
		if (!this.authService.isLoggedIn()) return;
		//TODO: If not logged in, wait until a login is complete.
		
		this.updatingRepairFacilities = true;
		this.userService.getRepairFacilities()
			.pipe(takeUntil(this._destroying$))
			.subscribe({
				next: value => {
					if (value.length > 0) this.userCache.setRepairFacilities(value);
					this.updatingRepairFacilities = false;
				},
				error: error => {
					this.updatingRepairFacilities = false;
					throw (error);
				}
			});
	}

	public refreshContacts(): void {
		// If not logged in, don't perform a refresh.
		if (!this.authService.isLoggedIn()) return;
		//TODO: If not logged in, wait until a login is complete.
		
		this.updatingContacts = true;
		this.userService.getContacts()
			.pipe(takeUntil(this._destroying$))
			.subscribe({
				next: value => {
					if (value.length > 0) this.userCache.setContacts(value);
					this.updatingContacts = false;
				},
				error: error => {
					this.updatingContacts = false;
					throw (error);
				}
			});
	}
}